import { lazy } from 'react'
import authRoute from './authRoute'
import openRoute from './openRoute'
import othersRoute from './othersRoute'
import { ORG_ADMINS } from '@/constants/roles.constant'
import type { Routes } from '@/@types/routes'

export const publicRoutes: Routes = [...authRoute]

/** Reachable in both states; see `openRoute.ts`. */
export const openRoutes: Routes = [...openRoute]

/**
 * Everything behind sign-in.
 *
 * `authority: []` means "any signed-in user". A non-empty list is checked by
 * AuthorityGuard against the roles the API returns on the user object — which
 * is a UI convenience, never an access control decision. The API is the guard
 * (CLAUDE.md §2.1); a route hidden here is still fully reachable by anyone
 * who types the URL or calls the endpoint directly.
 *
 * `ORG_ADMINS` on Rules, Connectors and Destinations, matching their nav
 * items: a member who typed `/connectors` got a page whose every button
 * refused them (QA DEV-176), and now gets the access-denied page instead.
 * Billing is deliberately not here — see its page.
 */
export const protectedRoutes: Routes = [
    {
        // The landing page (appConfig.authenticatedEntryPath). Built from the
        // same user-scoped cross-client reads as /meetings and /actions, so
        // there is no new endpoint here and nothing that widens what a request
        // can see.
        key: 'dashboard',
        path: '/dashboard',
        component: lazy(() => import('@/views/dashboard/DashboardPage')),
        authority: [],
    },
    {
        // Not nested under a client, because the whole point is seeing every
        // client's meetings at once — the client is a filter the server
        // verifies, never a claim the caller gets to assert.
        key: 'meetings',
        path: '/meetings',
        component: lazy(() => import('@/views/meetings/MeetingsPage')),
        authority: [],
    },
    {
        // Booking links (2026-10-15): the pages outsiders book appointments
        // on, and the appointments they made — across every client, like
        // /meetings, with the client a server-verified filter.
        key: 'bookings',
        path: '/bookings',
        component: lazy(() => import('@/views/booking/BookingsPage')),
        authority: [],
    },
    {
        key: 'bookingLinkNew',
        path: '/bookings/links/new',
        component: lazy(() => import('@/views/booking/LinkEditor')),
        authority: [],
    },
    {
        key: 'bookingLinkEdit',
        path: '/bookings/links/:id',
        component: lazy(() => import('@/views/booking/LinkEditor')),
        authority: [],
    },
    {
        key: 'bookingSchedules',
        path: '/bookings/schedules',
        component: lazy(() => import('@/views/booking/SchedulesPage')),
        authority: [],
    },
    {
        // The user's own commitments across every client. Like /meetings, not
        // nested under a client: the client is a server-verified filter, and
        // the whole point is seeing one list rather than six.
        key: 'actions',
        path: '/actions',
        component: lazy(() => import('@/views/actions/MyActionsPage')),
        authority: [],
    },
    {
        // One box over everything the user owns. Like /meetings and /actions,
        // not nested under a client: searching every client at once is the
        // point, and the client is a server-verified filter in the query
        // string rather than a path segment.
        key: 'search',
        path: '/search',
        component: lazy(() => import('@/views/search/GlobalSearchPage')),
        authority: [],
    },
    {
        key: 'analytics',
        path: '/analytics',
        component: lazy(() => import('@/views/Home')),
        authority: [],
    },
    {
        // Correction rules (DEV-136). Like /meetings, not nested under a
        // client: the page shows every client's rules with the client as a
        // server-verified filter in the URL.
        key: 'rules',
        path: '/rules',
        component: lazy(() => import('@/views/rules/RulesPage')),
        authority: ORG_ADMINS,
    },
    {
        // Notebooks (the listing). Like /meetings and /rules, not nested under
        // a client: the page exists so a developer with six clients sees one
        // list instead of six, with every row labelled by its client.
        key: 'notebooks',
        path: '/notebooks',
        component: lazy(() => import('@/views/notebooks/NotebooksPage')),
        authority: [],
    },
    {
        // One notebook's contents, which ARE client data and so live behind
        // the client path segment — the isolation boundary the API verifies
        // before any query.
        key: 'notebooks',
        path: '/clients/:clientId/notebooks/:notebookId',
        component: lazy(() => import('@/views/notebooks/NotebookDetail')),
        authority: [],
    },
    {
        // The knowledge graph: one client's people, organisations, systems,
        // workstreams and risks. Behind the client segment and reached from
        // the client page, never from the nav — a graph is one client's
        // (CLAUDE.md §4), and there is no cross-client list to put in a menu.
        key: 'clients',
        path: '/clients/:clientId/entities',
        component: lazy(() => import('@/views/entities/EntitiesPage')),
        authority: [],
    },
    {
        key: 'clients',
        path: '/clients/:clientId/entities/:entityId',
        component: lazy(() => import('@/views/entities/EntityDetail')),
        authority: [],
    },
    {
        // Trackers (the listing): tables the user defines, kept current by
        // their meetings. Like /notebooks, not nested under a client: one list
        // instead of six, "all my clients" trackers first, every entry
        // labelled by its client and the client a server-verified filter.
        key: 'trackers',
        path: '/trackers',
        component: lazy(() => import('@/views/trackers/TrackersPage')),
        authority: [],
    },
    {
        // A new tracker started from the listing: the first step asks "every
        // client, or one?" and one client continues under its own path below.
        key: 'trackers',
        path: '/trackers/new',
        component: lazy(() => import('@/views/trackers/TrackerEditor')),
        authority: [],
    },
    {
        // An "all my clients" tracker read as one table. Read-only: every row
        // names its client, and every action on it goes to that client's own
        // route — the family id never addresses a write.
        key: 'trackers',
        path: '/trackers/families/:familyId',
        component: lazy(() => import('@/views/trackers/FamilyPage')),
        authority: [],
    },
    {
        // One tracker's table, which IS client data and so lives behind the
        // client path segment — the isolation boundary the API verifies
        // before any query. Keyed `trackers` so the nav highlights Trackers.
        key: 'trackers',
        path: '/clients/:clientId/trackers/new',
        component: lazy(() => import('@/views/trackers/TrackerEditor')),
        authority: [],
    },
    {
        key: 'trackers',
        path: '/clients/:clientId/trackers/:trackerId',
        component: lazy(() => import('@/views/trackers/TrackerPage')),
        authority: [],
    },
    {
        key: 'trackers',
        path: '/clients/:clientId/trackers/:trackerId/edit',
        component: lazy(() => import('@/views/trackers/TrackerEditor')),
        authority: [],
    },
    {
        // The client's own tools — Jira, Linear, Slack, Gmail. Sits beside
        // Rules for the same reason it is next to it in the nav: both are
        // "teach the pipeline about this client" configuration rather than
        // something you read. Not nested under a client for the same reason
        // Rules is not: the client is a server-verified filter in the URL, and
        // the OAuth round trip needs a stable path to return to.
        key: 'connectors',
        path: '/connectors',
        component: lazy(() => import('@/views/connectors/ConnectorsPage')),
        authority: ORG_ADMINS,
    },
    {
        // The client's ticket cache — what their meetings' tracker tickets
        // say, and where each was discussed. A READ page, but scoped like
        // Connectors: the client is a server-verified `?client=` filter, and
        // there is deliberately no all-clients view.
        key: 'tickets',
        path: '/tickets',
        component: lazy(() => import('@/views/tickets/ClientTicketsPage')),
        authority: [],
    },
    {
        // Where a finished meeting is sent. Beside Connectors because it reads
        // the grants Connectors creates, and not nested under a client for the
        // same reason neither of those is: the client is a server-verified
        // filter in the URL, so a reload and a shared link both keep it.
        key: 'destinations',
        path: '/destinations',
        component: lazy(() => import('@/views/destinations/DestinationsPage')),
        authority: ORG_ADMINS,
    },
    {
        // The assistant (DEV-129): the conversation list...
        key: 'messaging',
        path: '/messaging',
        component: lazy(() => import('@/views/messaging/MessagingPage')),
        authority: [],
    },
    {
        // ...and one conversation ("new" starts one). User-scoped like the
        // list — the server resolves ownership, and a per-client
        // conversation's writes still travel its client-verified path.
        key: 'messaging',
        path: '/messaging/:chatId',
        component: lazy(() => import('@/views/messaging/MessagingPage')),
        authority: [],
    },
    {
        // Was the landing page before Meetings took over. Kept as a redirect
        // rather than deleted: it is in browser history and bookmarks, and a
        // dead link to your own dashboard is a bad first impression for the
        // gatekeeper cohort (root CLAUDE.md §7).
        key: 'analytics',
        path: '/home',
        component: lazy(() => import('@/views/Home/HomeRedirect')),
        authority: [],
    },
    {
        // First-sign-in onboarding: welcome, two optional research questions,
        // plan choice. Every new account lands here once (AuthProvider, on
        // `isNewUser`). Not in the nav: it is a one-time step, and everything
        // it asks is editable in Settings and Billing forever after.
        key: 'welcome',
        path: '/welcome',
        component: lazy(() => import('@/views/onboarding/Welcome')),
        authority: [],
        meta: {
            pageBackgroundType: 'plain',
            pageContainerType: 'contained',
        },
    },
    {
        key: 'settings',
        path: '/settings',
        component: lazy(() => import('@/views/settings/Settings')),
        authority: [],
    },
    {
        // A sibling rather than a tab on Settings: it has its own data, and it
        // is the return target for Stripe Checkout, which needs a stable URL.
        key: 'settings.billing',
        path: '/settings/billing',
        component: lazy(() => import('@/views/settings/Billing')),
        authority: [],
    },
    {
        // Recording storage (plans v2): what is taking the account's hours, and
        // deleting recordings to free them. A sibling like Billing, which links
        // here from its storage meter and from the "storage full" mail.
        key: 'settings.storage',
        path: '/settings/storage',
        component: lazy(() => import('@/views/settings/Storage')),
        authority: [],
    },
    {
        // The account: who is in it, who may be invited, the owner's switches.
        // A sibling like Billing, plus one reason of its own — it is where an
        // invitation's recipient lands after accepting, so the URL is linked
        // from an email.
        //
        // `authority: []` on purpose. A member reaching this page sees it with
        // the management controls absent, which is the honest rendering of what
        // they can do; an access-denied redirect would be a dead end on a page
        // that legitimately shows them their colleagues.
        key: 'settings.organization',
        path: '/settings/organization',
        component: lazy(() => import('@/views/settings/Organization')),
        authority: [],
    },
    {
        // A sibling for the same reason as Billing, plus one of its own: this is
        // the URL the desktop app sends people to, with `?code=` on it, so it
        // has to be linkable from outside the app entirely.
        key: 'settings.devices',
        path: '/settings/devices',
        component: lazy(() => import('@/views/settings/Devices')),
        authority: [],
    },
    {
        // Two-factor and "sign out everywhere". A sibling like Devices, and the
        // sign-in alert mail names it ("Settings → Security") as where to go
        // when a sign-in was not yours, so the path must not move.
        key: 'settings.security',
        path: '/settings/security',
        component: lazy(() => import('@/views/settings/Security')),
        authority: [],
    },
    {
        // Connected AI clients (Claude, ChatGPT). A sibling like Devices, and for
        // the same kind of reason: it manages credentials owned by the person
        // rather than by any one client.
        key: 'settings.connections',
        path: '/settings/connections',
        component: lazy(() => import('@/views/settings/Connections')),
        authority: [],
    },
    {
        // The MCP consent screen. **The API redirects a browser here** from
        // /authorize with `?request=`, so this URL is part of the OAuth flow and
        // cannot move without changing what the server sends people to
        // (MCP_CONSENT_PATH).
        //
        // Protected, which is the point — the decision has to be made by somebody
        // signed in. Arriving logged out works because ProtectedRoute preserves
        // the query string through the sign-in round trip.
        key: 'mcp.consent',
        path: '/mcp/consent',
        component: lazy(() => import('@/views/mcp/Consent')),
        authority: [],
    },
    {
        // Account → Alerts: what needs the person to act. Its own route rather
        // than a Settings card because the badge in the nav links straight to
        // it, and because its rows deep-link elsewhere (a client, Settings).
        key: 'alerts',
        path: '/alerts',
        component: lazy(() => import('@/views/alerts/AlertsPage')),
        authority: [],
    },
    {
        key: 'clients',
        path: '/clients',
        component: lazy(() => import('@/views/clients/ClientList')),
        authority: [],
    },
    {
        // Nested under a client id because that is the isolation boundary —
        // the API verifies it before any query, so it belongs in the path
        // rather than in component state.
        key: 'clients',
        path: '/clients/:clientId',
        component: lazy(() => import('@/views/clients/ClientDetail')),
        authority: [],
    },
    {
        key: 'clients',
        path: '/clients/:clientId/meetings/:meetingId',
        component: lazy(() => import('@/views/meetings/MeetingDetail')),
        authority: [],
    },
    {
        // The checklist on its own, for keeping beside the Meet/Teams window
        // during a call. Gutterless and footerless because it is opened into a
        // ~420px window, where the side nav has already collapsed.
        key: 'clients',
        path: '/live/clients/:clientId/meetings/:meetingId',
        component: lazy(() => import('@/views/meetings/LiveChecklist')),
        authority: [],
        meta: { pageContainerType: 'gutterless', footer: false },
    },
    ...othersRoute,
]
